import {
  getSiteContentById,
  siteContent,
  type SiteContentItem,
} from './siteContent';
import type { NavigateFunction } from 'react-router-dom';
import { isLivingWebsiteSalesQuery } from './livingWebsiteSales';
import { isBuildQuoteQuery } from './buildQuoteSales';
import { isStandaloneShareableRoute } from './avatarEmbed';
import {
  setAvatarSplitContent,
  resetAvatarSplitContent,
  getAvatarSplitId,
  bumpAvatarSplitReload,
} from './avatarSplit';

export type AvatarPanelHandler = (id: string | null) => void;
export type AvatarPageFrameHandler = (id: string | null) => void;
export type AvatarModalHandler = (type: 'contact' | 'partner' | 'demo') => void;

/** Router state flag — bypasses homepage split interceptors (e.g. maximize button). */
export const AVATAR_FULL_NAV_FLAG = 'avatarFullNavigation';

export function isAvatarFullNavigationState(state: unknown): boolean {
  return (
    typeof state === 'object' &&
    state !== null &&
    (state as Record<string, unknown>)[AVATAR_FULL_NAV_FLAG] === true
  );
}

export function getHistoryNavigationState(data: unknown): unknown {
  if (typeof data === 'object' && data !== null && 'usr' in data) {
    return (data as { usr?: unknown }).usr;
  }
  return undefined;
}

let panelHandler: AvatarPanelHandler | null = null;
let pageFrameHandler: AvatarPageFrameHandler | null = null;
let contactHandler: AvatarModalHandler | null = null;

/** Topics that should open a different page in the split iframe (e.g. concept → product page). */
const SPLIT_PAGE_ALIASES: Record<string, string> = {
  'living-websites': 'living-website',
};

export function resolveSplitPageId(pageId: string): string {
  return SPLIT_PAGE_ALIASES[pageId] ?? pageId;
}

export function shouldShowPageFrame(item: SiteContentItem): boolean {
  if (item.splitInIframe && item.ctaHref.startsWith('http')) return true;
  if (item.ctaAction === 'contact' || item.ctaAction === 'partner') return false;
  if (item.sourceType === 'homepage-section') return false;
  if (!item.existingRouteExists) return false;
  return item.sourceType === 'existing-page';
}

/**
 * Split-eligible: same-origin routes load in AvatarStaticSplitPanel iframe.
 * Not split-eligible: contact/partner (modal), homepage-section, missing routes.
 * External URLs (e.g. SELF OS → useselfos.com): new tab, no iframe.
 */
export function isSplitEligiblePageId(pageId: string): boolean {
  const resolved = resolveSplitPageId(pageId);
  const item = getSiteContentById(resolved);
  if (!item || !shouldShowPageFrame(item)) return false;
  if (item.splitInIframe && item.ctaHref.startsWith('http')) return true;
  if (item.ctaHref.startsWith('http')) {
    try {
      return new URL(item.ctaHref).origin === window.location.origin;
    } catch {
      return false;
    }
  }
  return true;
}

/** Map Manifest tool targets → siteContent id (siteContent + avatar-site-map.json). */
export function topicTargetToPageId(target: string): string | null {
  const raw = target.replace(/^#/, '').trim();
  if (!raw || raw === 'panel-body' || raw === 'detail-panel') return null;
  return resolveSectionIdFromManifestTarget(raw) ?? null;
}

/** Open the visible split panel for a Manifest target — same path as Explore topics chips. */
export function applySplitPanelForTopic(target: string): boolean {
  if (!isAvatarHomeSplitContext()) return false;

  const rawPageId = topicTargetToPageId(target);
  if (!rawPageId) return false;

  const pageId = resolveSplitPageId(rawPageId);
  const item = getSiteContentById(pageId);
  if (!item) return false;

  if (item.ctaAction === 'contact') {
    contactHandler?.('contact');
    return false;
  }
  if (item.ctaAction === 'partner') {
    contactHandler?.('partner');
    return false;
  }
  if (item.ctaHref.startsWith('http')) {
    try {
      if (item.splitInIframe) {
        openSitePageFrame(pageId);
        return true;
      }
      if (new URL(item.ctaHref).origin !== window.location.origin) {
        window.open(item.ctaHref, '_blank', 'noopener,noreferrer');
        return false;
      }
    } catch {
      return false;
    }
  }
  if (!shouldShowPageFrame(item)) return false;

  openSitePageFrame(pageId);
  return true;
}

export function registerAvatarPageFrameHandler(handler: AvatarPageFrameHandler): () => void {
  pageFrameHandler = handler;
  return () => {
    if (pageFrameHandler === handler) pageFrameHandler = null;
  };
}

export function openSitePageFrame(sectionId: string): SiteContentItem | undefined {
  const pageId = resolveSplitPageId(sectionId);
  const item = getSiteContentById(pageId);
  if (item && shouldShowPageFrame(item)) {
    const changed = pageId !== getAvatarSplitId();
    setAvatarSplitContent(pageId);
    if (!changed) bumpAvatarSplitReload();
    pageFrameHandler?.(pageId);
    panelHandler?.(null);
  }
  return item;
}

export function closeSitePageFrame(): void {
  resetAvatarSplitContent();
  pageFrameHandler?.(null);
}

/** True when the avatar homepage should keep Milan visible and show pages beside him. */
export function isAvatarHomeSplitContext(): boolean {
  const path = window.location.pathname.replace(/\/$/, '') || '/';
  return path === '/';
}

export function getSectionIdForPath(path: string): string | undefined {
  const normalized = normalizeContentPath(path);
  const matches = siteContent.filter((item) => {
    const href = normalizeContentPath(item.ctaHref);
    const route = normalizeContentPath(item.route);
    return href === normalized || route === normalized;
  });
  if (matches.length === 0) return undefined;

  const routePage = matches.find(
    (item) =>
      normalizeContentPath(item.route) === normalized && item.sourceType === 'existing-page'
  );
  if (routePage) return routePage.id;

  const anyPage = matches.find((item) => item.sourceType === 'existing-page');
  if (anyPage) return anyPage.id;

  return matches[0].id;
}

/** Normalize paths for matching (slashes, query, hash). */
export function normalizeContentPath(path: string): string {
  let value = path.trim();
  try {
    if (value.includes('://')) {
      value = new URL(value).pathname;
    }
  } catch {
    /* keep raw value */
  }
  value = value.split('?')[0].split('#')[0];
  if (!value.startsWith('/')) value = `/${value}`;
  if (value.length > 1 && value.endsWith('/')) value = value.slice(0, -1);
  return value;
}

function stripManifestDetailSuffix(id: string): string {
  return id.endsWith('-detail') ? id.slice(0, -'-detail'.length) : id;
}

/** Map Milan tool targets like "SELF Phone", "/self-phone", "self-phone-detail" → siteContent id */
export function resolveSectionIdFromManifestTarget(raw: string): string | undefined {
  const decoded = decodeURIComponent(raw).trim();
  if (!decoded) return undefined;

  try {
    const pathForStandalone = decoded.includes('://')
      ? new URL(decoded).pathname
      : decoded.startsWith('/')
        ? decoded
        : `/${decoded}`;
    if (isStandaloneShareableRoute(normalizeContentPath(pathForStandalone))) {
      return undefined;
    }
  } catch {
    /* ignore malformed URLs */
  }

  if (isLivingWebsiteSalesQuery(decoded)) return 'living-website';
  if (/\b(book|founder)\s+(a\s+)?consultation\b|\bconsultation\b|\boffice hours\b|\bbook a call\b|\b\$100\s*\/?\s*hr\b/i.test(decoded)) {
    return 'consultation';
  }
  if (isBuildQuoteQuery(decoded)) return 'build';

  try {
    if (decoded.includes('://')) {
      const parsed = new URL(decoded);
      const fromUrl = getSectionIdForPath(parsed.pathname);
      if (fromUrl) return fromUrl;
      const fromHref = siteContent.find((item) => item.ctaHref === decoded)?.id;
      if (fromHref) return fromHref;
    }
  } catch {
    /* ignore malformed URLs */
  }

  const fromPath = getSectionIdForPath(
    decoded.startsWith('/') || decoded.includes('://') ? decoded : `/${decoded}`
  );
  if (fromPath) return fromPath;

  const label = decoded.replace(/^\//, '');
  const baseId = stripManifestDetailSuffix(label);
  const fromDetail = getSiteContentById(baseId);
  if (fromDetail) return fromDetail.id;

  const slug = label.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
  const slugBase = stripManifestDetailSuffix(slug);
  if (slugBase === 'milan' || slug === 'milan') return 'milan-cheeks';

  const fromSlug = siteContent.find((item) => item.id === slug || item.id === slugBase);
  if (fromSlug) return fromSlug.id;

  const lower = label.toLowerCase();
  const fromTitle = siteContent.find(
    (item) =>
      item.title.toLowerCase() === lower ||
      item.id.replace(/-/g, ' ') === lower ||
      item.keywords.some((k) => k === lower || lower.includes(k))
  );
  if (fromTitle) {
    if (fromTitle.id === 'living-websites' && /show|see|open|preview|visit/i.test(lower)) {
      return 'living-website';
    }
    return fromTitle.id;
  }

  return getSiteContentById(decoded)?.id ?? getSiteContentById(slug)?.id ?? getSiteContentById(slugBase)?.id;
}

/** If Milan navigated to a bad path like /SELF Phone, recover the intended topic id */
export function recoverSplitSectionFromPathname(pathname: string): string | undefined {
  if (isStandaloneShareableRoute(pathname)) return undefined;
  const sectionId = resolveSectionIdFromManifestTarget(pathname);
  if (!sectionId) return undefined;
  const pageId = resolveSplitPageId(sectionId);
  const item = getSiteContentById(pageId);
  if (!item || !shouldShowPageFrame(item)) return undefined;
  return pageId;
}

/** Open split-screen page preview on `/` instead of SPA navigation. */
export function openSplitScreenForSection(sectionId: string): boolean {
  if (!isAvatarHomeSplitContext()) return false;
  const resolved = resolveSplitPageId(
    resolveSectionIdFromManifestTarget(sectionId) ?? sectionId
  );
  const item = getSiteContentById(resolved);
  if (!item || !shouldShowPageFrame(item)) return false;
  openSitePageFrame(resolved);
  return true;
}

function resolveSameOriginPath(url: string): string | null {
  try {
    if (url.startsWith('/')) return url.split('?')[0].split('#')[0] || url;
    const parsed = new URL(url, window.location.origin);
    if (parsed.origin !== window.location.origin) return null;
    return parsed.pathname;
  } catch {
    return null;
  }
}

/** Route Milan navigation / labels / paths into the static right panel on `/`. */
export function openHomeSplitForRequest(raw: string): boolean {
  return forceHomeSplitFromUrl(raw);
}

/** Always try to load a site page in the homepage split panel. */
export function forceHomeSplitFromUrl(raw: string): boolean {
  if (!isAvatarHomeSplitContext()) return false;

  const decoded = decodeURIComponent(raw).trim();
  if (!decoded || decoded === '/' || decoded === `${window.location.origin}/`) {
    return false;
  }

  const sectionId = resolveSectionIdFromManifestTarget(decoded);
  if (sectionId && openSplitScreenForSection(sectionId)) return true;

  const path = normalizeContentPath(decoded);
  if (path && path !== '/') {
    const fromPath = resolveSectionIdFromManifestTarget(path);
    if (fromPath && openSplitScreenForSection(fromPath)) return true;
  }

  if (openSplitScreenForPath(decoded)) return true;

  const matches = searchSiteContent(decoded);
  if (matches[0] && openSplitScreenForSection(matches[0].id)) return true;

  return false;
}

export function openSplitScreenForPath(path: string): boolean {
  const sectionId = resolveSectionIdFromManifestTarget(path) ?? getSectionIdForPath(path);
  if (!sectionId) return false;
  return openSplitScreenForSection(sectionId);
}

const HIGHLIGHT_CLASS = 'avatar-highlight-pulse';
const HIGHLIGHT_DURATION_MS = 3000;

export function registerAvatarPanelHandler(handler: AvatarPanelHandler): () => void {
  panelHandler = handler;
  return () => {
    if (panelHandler === handler) panelHandler = null;
  };
}

export function registerAvatarModalHandler(handler: AvatarModalHandler): () => void {
  contactHandler = handler;
  return () => {
    if (contactHandler === handler) contactHandler = null;
  };
}

export function getSiteContent(sectionId: string): SiteContentItem | undefined {
  return getSiteContentById(sectionId);
}

export function openSitePanel(sectionId: string): SiteContentItem | undefined {
  const item = getSiteContentById(sectionId);
  if (!item) return undefined;

  if (shouldShowPageFrame(item)) {
    pageFrameHandler?.(sectionId);
    panelHandler?.(null);
    return item;
  }

  pageFrameHandler?.(null);
  panelHandler?.(sectionId);
  return item;
}

export function closeSitePanel(): void {
  panelHandler?.(null);
  pageFrameHandler?.(null);
}

export function navigateToSitePage(
  sectionId: string,
  navigate: NavigateFunction,
  options?: { forceFullNavigation?: boolean }
): SiteContentItem | undefined {
  const item = getSiteContentById(sectionId);
  if (!item) return undefined;

  if (item.ctaAction === 'contact') {
    contactHandler?.('contact');
    return item;
  }
  if (item.ctaAction === 'partner') {
    contactHandler?.('partner');
    return item;
  }
  if (item.ctaAction === 'demo') {
    contactHandler?.('demo');
    if (!options?.forceFullNavigation && openSplitScreenForSection(sectionId)) return item;
    if (item.route.startsWith('http')) {
      window.open(item.ctaHref, '_blank', 'noopener,noreferrer');
    } else if (options?.forceFullNavigation) {
      navigate(item.ctaHref, { state: { [AVATAR_FULL_NAV_FLAG]: true } });
    } else {
      navigate(item.ctaHref);
    }
    return item;
  }
  if (!options?.forceFullNavigation && openSplitScreenForSection(sectionId)) return item;
  if (item.ctaAction === 'external' || item.ctaHref.startsWith('http')) {
    window.open(item.ctaHref, '_blank', 'noopener,noreferrer');
    return item;
  }

  if (options?.forceFullNavigation) {
    navigate(item.ctaHref, { state: { [AVATAR_FULL_NAV_FLAG]: true } });
  } else {
    navigate(item.ctaHref);
  }
  return item;
}

export function scrollToSiteSection(sectionId: string): SiteContentItem | undefined {
  const item = getSiteContentById(sectionId);
  const targetId = item?.sectionId ?? sectionId;
  const el =
    document.getElementById(targetId) ||
    document.querySelector(`[data-avatar-target="${targetId}"]`);

  if (el) {
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
  return item;
}

export function highlightSiteSection(sectionId: string): SiteContentItem | undefined {
  const item = scrollToSiteSection(sectionId);
  const targetId = item?.sectionId ?? sectionId;
  const el =
    document.getElementById(targetId) ||
    document.querySelector(`[data-avatar-target="${targetId}"]`);

  if (el) {
    el.classList.add(HIGHLIGHT_CLASS);
    window.setTimeout(() => el.classList.remove(HIGHLIGHT_CLASS), HIGHLIGHT_DURATION_MS);
  }
  return item;
}

function scoreMatch(item: SiteContentItem, query: string): number {
  const q = query.toLowerCase().trim();
  if (!q) return 0;

  let score = 0;
  if (item.id === q || item.id.replace(/-/g, ' ') === q) score += 100;
  if (item.title.toLowerCase() === q) score += 90;
  if (item.route.toLowerCase().includes(q)) score += 40;

  for (const kw of item.keywords) {
    const k = kw.toLowerCase();
    if (k === q) score += 80;
    else if (k.includes(q) || q.includes(k)) score += 50;
    else if (q.split(/\s+/).some((word) => k.includes(word))) score += 20;
  }

  if (item.title.toLowerCase().includes(q)) score += 30;
  if (item.shortDescription.toLowerCase().includes(q)) score += 10;

  return score;
}

export function searchSiteContent(query: string): SiteContentItem[] {
  const q = query.toLowerCase().trim();
  if (!q) return [];

  if (isBuildQuoteQuery(q)) {
    const build = getSiteContentById('build');
    if (build) {
      return [
        build,
        ...siteContent.filter((s) => s.id !== 'build' && scoreMatch(s, q) > 0),
      ];
    }
  }

  if (isLivingWebsiteSalesQuery(q)) {
    const living = getSiteContentById('living-website');
    if (living) {
      return [
        living,
        ...siteContent.filter((s) => s.id !== 'living-website' && scoreMatch(s, q) > 0),
      ];
    }
  }

  const intentMap: Record<string, string> = {
    milan: 'milan-cheeks',
    'milan cheeks': 'milan-cheeks',
    'who is milan': 'milan-cheeks',
    founder: 'milan-cheeks',
    ceo: 'milan-cheeks',
    phone: 'self-phone',
    'ai phone': 'self-phone',
    'operating system': 'self-os',
    os: 'self-os',
    sound: 'resonatia',
    frequency: 'resonatia',
    resonance: 'resonatia',
    resonatia: 'resonatia',
    essence: 'essence',
    'essence token': 'essence',
    essense: 'essence',
    settlement: 'essence',
    'role model': 'role-model-ai',
    'role model ai': 'role-model-ai',
    concierge: 'role-model-ai',
    'concierge 2.0': 'role-model-ai',
    'concierge 2.3': 'role-model-ai',
    rolemodel: 'role-model-ai',
    aura: 'aura-resonant-layer',
    'resonant layer': 'aura-resonant-layer',
    'aura resonant layer': 'aura-resonant-layer',
    harmony: 'aura-resonant-layer',
    encoder: 'harmony-encoder',
    'harmony encoder': 'harmony-encoder',
    products: 'products',
    catalog: 'products',
    'show me living website': 'living-website',
    'show living website': 'living-website',
    'living website': 'living-website',
    build: 'build',
    quote: 'build',
    'get a quote': 'build',
    'custom build': 'build',
    'mobile app': 'build',
    consultation: 'consultation',
    'book consultation': 'consultation',
    'book a consultation': 'consultation',
    'founder consultation': 'consultation',
    'book a call': 'consultation',
    'office hours': 'consultation',
    '$100/hr': 'consultation',
    'living websites': 'living-websites',
    'how can i get my own': 'living-website',
    'get my own': 'living-website',
    'my own living website': 'living-website',
    'get a living website': 'living-website',
    'buy living website': 'living-website',
    'avatar for my site': 'living-website',
    'avatar for my business': 'living-website',
    'avatar for my website': 'living-website',
    'one like this': 'living-website',
    'like this site': 'living-website',
    'want one like': 'living-website',
    'build me a living website': 'living-website',
    'i want a living website': 'living-website',
    'can i buy this': 'living-website',
    'living website pricing': 'living-website',
    'how do i get one': 'living-website',
    'how do i make you': 'living-website',
    'how can i make you': 'living-website',
    'how do i build you': 'living-website',
    'how do i create you': 'living-website',
    'how do i get a site like': 'living-website',
    'site like this': 'living-website',
    'website like this': 'living-website',
    'avatar like you': 'living-website',
    'how was this made': 'living-website',
    'how does this work': 'living-website',
    'put you on my site': 'living-website',
    avatar: 'ai-avatars',
    'website avatar': 'living-websites',
    demo: 'demo',
    partner: 'partnerships',
    invest: 'investors',
    investor: 'investors',
    investors: 'investors',
    'investor deck': 'investors',
    raising: 'investors',
    raise: 'investors',
    fundraising: 'investors',
    funding: 'investors',
    capital: 'investors',
    'are you raising': 'investors',
    'is self labs raising': 'investors',
    'looking for investors': 'investors',
    'pitch deck': 'investors',
    incubator: 'incubator',
    'ai incubator': 'incubator',
    accelerator: 'incubator',
    cohort: 'incubator',
    f6s: 'incubator',
    'startup program': 'incubator',
    creator: 'creator-tools',
    robot: 'robotics',
    brain: 'bci',
    'cerebral os': 'bci',
    cerebral: 'bci',
    'open source ide': 'bci',
    contact: 'contact',
    about: 'self-labs',
    ecosystem: 'products',
  };

  const directId = intentMap[q];
  if (directId) {
    const direct = getSiteContentById(directId);
    if (direct) return [direct];
  }

  for (const [term, id] of Object.entries(intentMap)) {
    if (q.includes(term)) {
      const item = getSiteContentById(id);
      if (item) return [item, ...siteContent.filter((s) => s.id !== id && scoreMatch(s, q) > 0)];
    }
  }

  return siteContent
    .map((item) => ({ item, score: scoreMatch(item, q) }))
    .filter(({ score }) => score > 0)
    .sort((a, b) => b.score - a.score)
    .map(({ item }) => item);
}

export function tryOpenAvatarWidget(_prompt?: string): boolean {
  const hero = document.getElementById('avatar-hero');
  if (hero) {
    window.dispatchEvent(new CustomEvent('selflabs:start-avatar-conversation'));
    hero.scrollIntoView({ behavior: 'smooth', block: 'start' });
    return true;
  }
  const btn = document.querySelector<HTMLButtonElement>(
    'button[aria-label*="Milan"], button[aria-label*="Open"]'
  );
  if (btn) {
    btn.click();
    return true;
  }
  return false;
}

function handleOpenSection(detail: { id?: string; manifest?: boolean }) {
  if (!detail?.id) return;
  if (applySplitPanelForTopic(detail.id) || openSplitScreenForSection(detail.id)) return;
  if (detail.manifest) return;
  openSitePanel(detail.id);
}

function handleShowPage(detail: { id?: string }) {
  if (detail?.id) {
    const item = getSiteContentById(detail.id);
    if (item && shouldShowPageFrame(item)) {
      openSitePageFrame(detail.id);
    } else {
      openSitePanel(detail.id);
    }
  }
}

function handleNavigateSection(
  detail: { id?: string },
  navigate?: (path: string) => void
) {
  if (!detail?.id) return;
  if (openSplitScreenForSection(detail.id)) return;
  if (navigate) {
    navigateToSitePage(detail.id, navigate);
  } else {
    const item = getSiteContentById(detail.id);
    if (item?.ctaHref.startsWith('http')) {
      window.open(item.ctaHref, '_blank', 'noopener,noreferrer');
    } else if (item) {
      window.location.href = item.ctaHref;
    }
  }
}

export function setupAvatarEventBridge(navigate?: (path: string) => void): () => void {
  const onOpen = (e: Event) =>
    handleOpenSection((e as CustomEvent<{ id: string }>).detail);
  const onShowPage = (e: Event) =>
    handleShowPage((e as CustomEvent<{ id: string }>).detail);
  const onNavigate = (e: Event) =>
    handleNavigateSection((e as CustomEvent<{ id: string }>).detail, navigate);
  const onScroll = (e: Event) => {
    const { id } = (e as CustomEvent<{ id: string }>).detail;
    if (id) scrollToSiteSection(id);
  };
  const onHighlight = (e: Event) => {
    const { id } = (e as CustomEvent<{ id: string }>).detail;
    if (id) highlightSiteSection(id);
  };
  const onDemo = () => {
    openSitePanel('demo');
    contactHandler?.('demo');
  };
  const onContact = () => {
    openSitePanel('contact');
    contactHandler?.('contact');
  };

  const onMessage = (e: MessageEvent) => {
    if (!e.origin.includes('manifestanything.ai') && !e.origin.includes('rolemodel.ai')) return;
    const data = e.data;
    if (!data || typeof data !== 'object') return;

    const id =
      data.sectionId ?? data.id ?? data.pageId ?? data.tool?.sectionId;
    const action = data.action ?? data.type ?? data.event;

    if (id && (action === 'open-section' || action === 'show-page' || action === 'navigate')) {
      if (!openSplitScreenForSection(String(id))) {
        openSitePanel(String(id));
      }
    }
    if (data.query && typeof data.query === 'string') {
      const matches = searchSiteContent(data.query);
      if (matches[0]) {
        if (!openSplitScreenForSection(matches[0].id)) {
          openSitePanel(matches[0].id);
        }
      }
    }
  };

  window.addEventListener('selflabs:open-section', onOpen);
  window.addEventListener('selflabs:show-page', onShowPage);
  window.addEventListener('selflabs:navigate-section', onNavigate);
  window.addEventListener('message', onMessage);
  window.addEventListener('selflabs:scroll-section', onScroll);
  window.addEventListener('selflabs:highlight-section', onHighlight);
  window.addEventListener('selflabs:request-demo', onDemo);
  window.addEventListener('selflabs:contact', onContact);

  return () => {
    window.removeEventListener('selflabs:open-section', onOpen);
    window.removeEventListener('selflabs:show-page', onShowPage);
    window.removeEventListener('selflabs:navigate-section', onNavigate);
    window.removeEventListener('message', onMessage);
    window.removeEventListener('selflabs:scroll-section', onScroll);
    window.removeEventListener('selflabs:highlight-section', onHighlight);
    window.removeEventListener('selflabs:request-demo', onDemo);
    window.removeEventListener('selflabs:contact', onContact);
  };
}
